/**
 * HardwareNumberPicker - type-to-search device picker for the location
 * dialogs (pour create wizard + pour edit page). Replaces the plain
 * "Hardware Number" select so a user can type "734", press Enter and move
 * straight on. Same look and keyboard behaviour as the Tank picker in
 * PourLocationsSection.
 *
 * The parent filters `options` (hardware type, availability, fully used
 * devices) and must keep the currently selected device in the list, or the
 * box shows empty.
 */
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';

export interface HardwareNumberOption {
  id: string;
  label: string;
}

export interface HardwareNumberPickerProps {
  options: HardwareNumberOption[];
  /** Selected device id, or '' for none. */
  value: string;
  /** Called with the picked device id, or '' when cleared. */
  onChange: (id: string) => void;
  disabled?: boolean;
  required?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string;
  noOptionsText?: string;
}

const HardwareNumberPicker: React.FC<HardwareNumberPickerProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  required = false,
  inputRef,
  label = 'Hardware Number',
  noOptionsText = 'No free devices',
}) => {
  const selected = value ? options.find((o) => o.id === value) || null : null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label}
      value={selected}
      onChange={(_, newValue) => onChange(newValue?.id || '')}
      renderOption={(props, option) => {
        // Key by id: two devices can share a display name.
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
        return (
          <li key={option.id} {...rest}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" required={required} inputRef={inputRef} />
      )}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      noOptionsText={noOptionsText}
      disabled={disabled}
      size="small"
      autoHighlight
      openOnFocus
      handleHomeEndKeys
      fullWidth
    />
  );
};

export default HardwareNumberPicker;
